import pkg from "../lib/pkg";
import { injectScript } from "../lib/util";
import {
  Integration,
  IntegrationCategory,
} from "../providers/AnalyticsContext";
import type { Context } from "../types";
import { AnalyticsPlugin, AnalyticsPluginType } from ".";

type OpenAiAdsQueue = {
  (command: "init", options: { pixelId: string }): void;
  q: IArguments[];
};

declare global {
  interface Window {
    oaiq?: OpenAiAdsQueue;
  }
}

export default class OpenAiAdsPlugin implements AnalyticsPlugin {
  name = Integration.OpenAiAds;
  category = IntegrationCategory.Advertising;
  type = AnalyticsPluginType.destination;
  version = "0.1.0";

  scriptId = "os-openai-ads";
  scriptSrc = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

  constructor(private pixelId = pkg.openAiAdsPixelId) {}

  isLoadable() {
    return Boolean(this.pixelId);
  }

  async load(): Promise<void> {
    if (!this.pixelId) {
      return;
    }

    if (!window.oaiq) {
      const queuedCalls: IArguments[] = [];
      const queue: OpenAiAdsQueue = Object.assign(
        function (_command: "init", _options: { pixelId: string }) {
          queuedCalls.push(arguments);
        },
        { q: queuedCalls },
      );
      window.oaiq = queue;
    }

    const scriptLoaded = injectScript(
      this.scriptId,
      this.scriptSrc,
      undefined,
      document.head,
    );
    window.oaiq("init", { pixelId: this.pixelId });
    await scriptLoaded;
  }

  async unload(): Promise<void> {
    document.getElementById(this.scriptId)?.remove();
    window.oaiq = undefined;
  }

  isLoaded() {
    return Boolean(
      typeof window !== "undefined" &&
        window.oaiq &&
        document.getElementById(this.scriptId),
    );
  }

  /*
   * This first phase only installs and initializes the SDK - no conversion
   * events are measured yet. Event mapping (e.g. `oaiq("measure", ...)`) will
   * be added in a follow up.
   */
  async track(context: Context): Promise<Context> {
    return context;
  }
}
